import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService, AppAuthError } from '../services/auth/auth.service';
import { from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

/**
 * JWT Interceptor (Functional Interceptor)
 *
 * Automatically injects the JWT access token into all outgoing HTTP requests.
 * Adds the Authorization header with Bearer token format.
 *
 * Flow:
 * 1. Intercepts all HTTP requests
 * 2. Fetches JWT token from AuthService
 * 3. Clones request with Authorization header
 * 4. Passes modified request to next handler
 *
 * If token fetch fails (user not authenticated), the request proceeds without
 * the Authorization header, allowing the error interceptor to handle 401 responses.
 *
 * Registration in app.config.ts:
 * ```typescript
 * provideHttpClient(
 *   withInterceptors([jwtInterceptor, errorInterceptor])
 * )
 * ```
 */
export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Convert Promise to Observable and get JWT token
  return from(authService.getJwtToken()).pipe(
    switchMap((jwt: string) => {
      // Clone request and add Authorization header
      const clonedRequest = req.clone({
        setHeaders: {
          Authorization: `Bearer ${jwt}`
        }
      });

      return next(clonedRequest);
    }),
    catchError((err) => {
      // If token fetch failed (not authenticated), let request proceed
      // without Authorization header - error interceptor will handle 401
      if (err instanceof AppAuthError) {
        console.warn('JWT token not available, proceeding without Authorization header');
        return next(req);
      } else {
        return throwError(() => err);
      }
    })
  );
};
